import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const completeSimulation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ runId: z.string().uuid(), fullName: z.string().min(2).max(120) }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: run } = await supabase
      .from("lxp_simulation_runs")
      .select("id,simulation_id,status")
      .eq("id", data.runId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!run) throw new Error("Run not found");

    // Compute average overall_percent across submitted tasks
    const { data: subs } = await supabase
      .from("lxp_task_submissions")
      .select("ai_scores")
      .eq("run_id", run.id);
    const scores = (subs ?? []).map((s: any) => Number(s.ai_scores?.overall_percent ?? 0)).filter((n) => Number.isFinite(n) && n > 0);
    const finalScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

    await supabase
      .from("lxp_simulation_runs")
      .update({ status: "completed", completed_at: new Date().toISOString(), final_score: finalScore })
      .eq("id", run.id);

    // Issue certificate (idempotent-ish: rely on the app not calling twice)
    const num = `LXP-${new Date().getFullYear()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const { data: cert, error } = await supabase
      .from("lxp_certificates")
      .insert({
        user_id: userId, simulation_id: run.simulation_id, certificate_number: num,
        full_name: data.fullName, final_score: finalScore,
      })
      .select("id,certificate_number,verification_token,issued_at,final_score")
      .single();
    if (error) throw new Error(error.message);
    return { certificate: cert };
  });

export const verifyCertificate = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ token: z.string().min(4).max(200) }).parse(i))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: rows, error } = await sb.rpc("lxp_verify_certificate", { _token: data.token });
    if (error) throw new Error(error.message);
    const cert = Array.isArray(rows) ? rows[0] : null;
    if (!cert) return { certificate: null };
    return {
      certificate: {
        full_name: cert.full_name,
        certificate_number: cert.certificate_number,
        issued_at: cert.issued_at,
        final_score: cert.final_score,
        simulation_title: cert.simulation_title ?? "",
      },
    };
  });

